import { twMerge } from 'tailwind-merge'
import { ProductGroupName } from '~/shared/constants'
import { ProductCode, type ProductContext } from '~/types'

export function Timeout(time = 100) {
	return new Promise(resolve => {
		const timeout = setTimeout(() => {
			resolve(true)
			clearTimeout(timeout)
		}, time)
	})
}
export async function resolve<T>(
	callback: () => T | Promise<T>,
	loading?: Ref<boolean>,
	timeout = 0
): Promise<T | null> {
	try {
		await Timeout(timeout)
		if (loading !== undefined) {
			loading.value = true
		}
		return await callback()
	} catch (error) {
		console.error(error)
		return null
	} finally {
		if (loading !== undefined) {
			loading.value = false
		}
	}
}
export function catchError<T>(callback: (...args: any[]) => T, shouldShowError = false) {
	try {
		return callback()
	} catch (error: any) {
		console.error(error)
		if (shouldShowError) {
			useNotification().alert({
				status: 500,
				message: `Something went wrong, please try again later: ${error.message}`
			})
		}
	}
}

export function chunkArray<T = any>(arr: T[], size: number = 3): T[] {
	const chunked = []
	for (let i = 0; i < arr.length; i += size) {
		chunked.push(arr.slice(i, i + size))
	}
	return chunked as T[]
}
export function reduceArray<T = any>(arr: T[], start = 0, size: number = 4): T[] {
	return arr.slice(start, size) as T[]
}
export function replaceLabels(arg: string) {
	const text = String(arg).toLowerCase()
	if (text === 'bi-annual') {
		return 'Bi-annually'
	} else if (text === 'annual') {
		return 'Annually'
	}
	return text
}
export function filterProducts(payload: ProductContext, showAiicoTravel = false, isBusiness = false) {
	const miniTargetCode = isBusiness
		? ProductCode.FLEXICARE_MINI_BASTION_BUSINESS
		: ProductCode.FLEXICARE_BASTION_PERSONAL
	const targetCode = isBusiness ? ProductCode.FLEXICARE_BASTION_BUSINESS : ProductCode.FLEXICARE_BASTION_PERSONAL

	const miniItem = payload.find(item => item.code_name === miniTargetCode)
	const miniPrice = miniItem ? parseInt(String(miniItem.market_price)) : 0

	const zencareTarget = isBusiness ? ProductCode.ZENCARE_BASTION_BUSINESS : ProductCode.ZENCARE_BASTION_PERSONAL
	const primecareTarget = isBusiness ? ProductCode.PRIMECARE_BASTION_BUSINESS : ProductCode.PRIMECARE_BASTION_PERSONAL

	const allowedCodes = new Set([
		zencareTarget,
		primecareTarget,
		ProductCode.FLEXICARE_BASTION_BUSINESS,
		ProductCode.FLEXICARE_BASTION_PERSONAL,
		ProductCode.FLEXICARE_HYGEIA_BUSINESS,
		ProductCode.GADGET,
		ProductCode.COMPREHENSIVE_AUTO,
		ProductCode.MINI_COMPREHENSIVE,
		ProductCode.THIRD_PARTY,
		ProductCode.TRAVEL_ALLIANZ,
		...(showAiicoTravel ? [ProductCode.TRAVEL_AIICO] : [])
	])

	return payload.reduce((acc, item) => {
		if (allowedCodes.has(item.code_name)) {
			if (item.group_id === ProductGroupName.FLEXICARE_BASTION_BUSINESS) {
				if (item.code_name === targetCode) {
					item.market_price = String(miniPrice)
					if (item.pricingBenefits?.price?.text !== undefined) {
						item.pricingBenefits.price.text = String(miniPrice)
					}
					if (item.pricingBenefits?.description?.text !== undefined) {
						item.pricingBenefits.description.text = item.pricingBenefits.description.text.replace(
							/(₦\s*)[\d,]+/g,
							`$1${miniPrice.toLocaleString()}`
						)
					}
				}
			}
			acc.push(item)
		}
		return acc
	}, [] as any) as ProductContext
}
export function getMaxMinPrice(key: string, productList: ProductContext) {
	let data = {
		max: 0,
		min: 0
	}
	const prices =
		productList?.filter(item => item.group_id === key).map(item => Number(String(item.market_price ?? 0))) || []

	if (prices.length > 0) {
		data = {
			max: Math.max(...prices),
			min: Math.min(...prices)
		}
	}

	return data
}
export function rename(name: string, oldName: string | string[], newName: string[] | string): string {
	if (Array.isArray(oldName)) {
		return Array.isArray(newName)
			? oldName.reduce((prev, curr, index) => prev.replace(curr, String(newName[index])), name)
			: oldName.reduce((prev, curr) => prev.replace(curr, String(newName)), name)
	}
	if (!Array.isArray(newName)) {
		return name.replace(oldName, String(newName))
	}
	return name.replace(oldName, String(newName[0]))
}
export function isValidJSON(str: string) {
	try {
		JSON.parse(str)
		return true
	} catch (e) {
		return false
	}
}
type GroupedResult<TItem, TMeta> = Record<string, TMeta & { list: TItem[] }>
export function GroupBy<TItem, TMeta>(
	array: TItem[],
	getGroupKey: (item: TItem) => string,
	getMetadata: (groupKey: string, groupItems: TItem[]) => TMeta
): GroupedResult<TItem, TMeta> {
	return array.reduce(
		(acc, item) => {
			const key = getGroupKey(item)

			if (!acc[key]) {
				const initialItems = [item]
				acc[key] = {
					...getMetadata(key, initialItems),
					list: initialItems
				}
			} else {
				acc[key].list.push(item)
			}

			return acc
		},
		{} as GroupedResult<TItem, TMeta>
	)
}
export function cn(...args: any[]) {
	return twMerge(...args)
}
